import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserRoundCog, ArrowRightLeft } from "lucide-react";

import { useTranslation } from "react-i18next";
export default function HandoverDialog({ allocation, open, onClose, onDone }) {
  const { t } = useTranslation();
  const [peers, setPeers] = useState([]);
  const [toUserId, setToUserId] = useState("");
  const [closing, setClosing] = useState("");
  const [testing, setTesting] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/attendance/live")
      .then((r) => {
        const active = (r.data.active || []).filter(
          (a) => a.user_id !== allocation?.user_id && a.shift_id === allocation?.shift_id,
        );
        setPeers(active);
      })
      .catch(() => setPeers([]));
    setToUserId("");
    setClosing("");
    setTesting("");
    setNote("");
  }, [open, allocation]);

  const submit = async () => {
    if (!toUserId) return toast.error(t("components.handoverdialog.pick_a_recipient_csa"));
    if (closing === "") return toast.error(t("components.handoverdialog.enter_the_current_meter_closing"));
    const c = Number(closing);
    const opening = Number(allocation?.opening || 0);
    if (Number.isNaN(c) || c < opening) {
      return toast.error(`Closing must be >= opening (${opening})`);
    }
    setSubmitting(true);
    try {
      await api.post("/nozzles/handover/request", {
        nozzle_allocation_id: allocation.id,
        to_user_id: toUserId,
        closing: c,
        testing: testing === "" ? 0 : Number(testing),
        note,
      });
      toast.success(t("components.handoverdialog.handover_requested_waiting_for_manager_a"));
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="handover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-primary" /> Handover nozzle
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
            <div className="font-mono font-semibold text-foreground">
              {allocation?.machine_code} · {allocation?.nozzle_code} ({allocation?.product_name})
            </div>
            <div className="mt-1 text-muted-foreground">
              Opening: <span className="font-mono">{allocation?.opening ?? "-"}</span> · Rate: <span className="font-mono">{allocation?.rate ?? "-"}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ho-to">{t("components.handoverdialog.hand_over_to")}</Label>
            <select
              id="ho-to"
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="handover-to"
            >
              <option value="">— Select CSA on same shift —</option>
              {peers.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.user_name} ({p.shift_name})
                </option>
              ))}
            </select>
            {peers.length === 0 && (
              <div className="text-[11px] text-muted-foreground">
                No other CSA is checked-in on your shift right now. Ask them to check in first.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ho-close">{t("components.handoverdialog.current_meter_closing")}</Label>
              <Input
                id="ho-close"
                inputMode="decimal"
                value={closing}
                onChange={(e) => setClosing(e.target.value)}
                placeholder="e.g. 1245.78"
                className="font-mono"
                data-testid="handover-closing"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ho-test">{t("components.handoverdialog.testing_l")}</Label>
              <Input
                id="ho-test"
                inputMode="decimal"
                value={testing}
                onChange={(e) => setTesting(e.target.value)}
                placeholder="0"
                className="font-mono"
                data-testid="handover-testing"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ho-note">{t("csa.csaportal.note_optional")}</Label>
            <Textarea
              id="ho-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("components.handoverdialog.why_the_handover_e_g_emergency_leave")}
              rows={2}
              data-testid="handover-note"
            />
          </div>

          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-800 dark:text-amber-300">
            <UserRoundCog className="mr-1 inline h-3 w-3" />
            Your manager must <b>approve</b> before the recipient can start selling on this nozzle.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="handover-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || peers.length === 0} data-testid="handover-submit">
            {submitting ? "Requesting..." : "Request handover"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
